import { Box
} from "@mui/material";
import { useCallback,useReducer, useState } from "react";
import { LocationService } from "../../../service/locationService";
import useDialogModal from "../../../hooks/useDialogModal";
import LocationInputForm from "./locationInputForm";
import EnhancedTable from "../../enhancedTable/EnhancedTable";
import useAbortController from "../../../hooks/useAbortController";
import { isLocalDev } from "../../../utils/environment";
import ConfirmationDialog from "../../ui/confirmationDialog";
import validLookup from "../../../utils/validLookup";
import { toast } from "react-toastify";
import LocationEditForm from "./locationEditForm";
import { locationColumns } from "./columns";
import EditIcon from "@mui/icons-material/Edit";
import { useCan } from "../../../hooks/useCan";

export default function ManageLocation() {
    const can = useCan();
    const [signal, abortRequest] = useAbortController();
    const [loading, setLoading] = useState(false);
    const [location, setLocation] = useState([]);
    const [LocationInputDialog, showLocationInputForm] = useDialogModal(LocationInputForm);
    const [LocationEditDialog, showLocationEditForm] = useDialogModal(LocationEditForm);
    const [
        ConfirmationDialogComp,
        showConfirmationDialog,
        closeConfirmationDialog,
    ] = useDialogModal(ConfirmationDialog);

    const [state, updateState] = useReducer((state, params) => {
        return { ...state, ...params };
    }, {
        locations: [],
        totalRowCount: 0,
        pageCount: 0,
        filter: '',
        loading: false,
    });

    const onAddRecordClicked = () => {
        showLocationInputForm();
    }

    const handleRowEdit = (rowData) => {
        showLocationEditForm(null, { location: rowData });
    }

    const locationAddHandler = (newLocation) => {
        const newData = state.locations.concat([newLocation]);
        updateState({ data: newData });
    }

    const handleLocationEdit = (location) => {
        // find the employee by row id
        const existingLocations = state.locations;
        const idx = existingLocations.findIndex(e => e.id === location.id);
        const updatedList = existingLocations.map((row, index) => {
            if (row.id === location.id) {
                return {
                    ...existingLocations[idx], ...location,
                }
            }
            return row
        });

        // update list
        updateState({
            locations: updatedList
        });
    }

    /**
     * fetch data
     */
    const fetchData = useCallback(({ pageSize, pageIndex, filters, sortBy }) => {
        const from = pageSize * pageIndex;
        updateState({ loading: true });
        LocationService.getWithLimit(from, pageSize, filters, sortBy, signal).then(result => {
            const { code, data } = result;
            if (code === 0) {
                updateState({
                    locations: data.data,
                    totalRowCount: data.count,
                    pageCount: Math.ceil(data.count / pageSize),
                });
            }
            updateState({ loading: false });
        }).catch(error => {
            if (isLocalDev) {
                console.log(error)
            }
            updateState({ loading: false });
        });
    }, []);

    /**
     * delete
     * @param {*} rowids 
     */
    const onDeleteLocation = (rowids) => {
        // Check if user has delete permission
        if (!can.section('locations', { action: 'delete' })) {
            toast.error('You do not have permission to delete locations');
            return;
        }

        showConfirmationDialog(async (action) => {
            if (action === validLookup.confirmationDialogAction.ok) {
                const deleteRecordResult = await LocationService.deleteLocation(rowids.map(x => parseInt(x, 10)));
                const { code, message, data } = deleteRecordResult;

                if (code === 0) {
                    toast.success(message);
                    const updateList = state.locations.filter(d => !rowids.map(x => parseInt(x, 10)).includes(d.id));
                    updateState({ locations: updateList });
                } else {
                    toast.error("Failed to delete record(s)");
                }
            }
            closeConfirmationDialog();
        }, {
            title: 'Delete record',
            message: `Please confirm if you would like to delete ${rowids.length} record(s)?`,
        });
    }

    // Check permissions for various actions
    const canDelete = can.section('locations', { action: 'delete' });
    const canCreate = can.section('locations', { action: 'create' });
    const canUpdate = can.section('locations', { action: 'edit' });

    return (
        <Box>
            <EnhancedTable
                columns={locationColumns}
                addRecordHandler={canCreate ? onAddRecordClicked : undefined}
                deleteRecordHandler={canDelete ? onDeleteLocation : undefined}
                data={state.locations}
                totalRowCount={state.totalRowCount}
                pageCount={state.pageCount}
                fetchData={fetchData}
                selectionMode="multi"
                loading={state.loading}
                actions={canUpdate ? [
                    {
                        icon: <EditIcon />,
                        onClick: handleRowEdit
                    },
                ] : []}
            />
            <LocationInputDialog onLocationAdded={locationAddHandler} />
            <LocationEditDialog onLocationEdit={handleLocationEdit} />
            <ConfirmationDialogComp
                label={{
                    positive: "Confirm",
                    negative: "Cancel",
                }} />
        </Box>

    )

}